import {parseFileAnchor} from '@github-ui/blob-anchor'
import {CodeNavigationInfo, type CodeSymbol} from '@github-ui/code-nav'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {BlobPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import type {RefInfo} from '@github-ui/repos-types'
import {parseDirectives} from '@github-ui/syntax-highlighted-lines/ast-utils'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {useCallback, useEffect, useMemo, useState} from 'react'

import type {CodeNavData} from '../components/blob/BlobContent/BlobContent'
import {isSymbol} from '../utilities/lines'
import {scrollLineIntoView} from './use-scroll-line-into-view'

const EMPTY_SYMBOLS: CodeSymbol[] = []
const EMPTY_RAW_LINES: string[] = []

export function useCodeNavigation(
  blob: BlobPayload,
  onCodeNavActivated: () => void,
  setValidCodeNav: (show: boolean) => void,
  hash: string,
  hasBlame: boolean,
) {
  const nav = useNavigate()
  const repo = useCurrentRepository()
  const user = useCurrentUser()
  const path = usePath()
  const refInfo = useRefInfo()

  const [isCodeNavLoading, setIsCodeNavLoading] = useState(false)

  // normalize the styling directives to be in the format expected by the code nav
  const normalizedStylingDirectives = useMemo(() => {
    return blob.stylingDirectives?.map(directiveLine => {
      return parseDirectives(directiveLine)
    })
  }, [blob.stylingDirectives])

  const [urlParams] = useSearchParams()
  const isPlain = urlParams.get('plain') === '1'

  // Cast CoreRefInfo to RefInfo for external code-nav package
  // The code-nav package only uses refInfo.name, so the missing fields are safe to omit
  const refInfoForCodeNav = refInfo as RefInfo
  const symbols = blob.symbols?.symbols ?? EMPTY_SYMBOLS

  const codeNavInfo = useMemo(() => {
    try {
      return new CodeNavigationInfo(
        repo,
        refInfoForCodeNav,
        path,
        !!user,
        blob.rawLines || EMPTY_RAW_LINES,
        symbols,
        normalizedStylingDirectives,
        blob.language,
        isPlain,
        setIsCodeNavLoading,
      )
    } catch {
      return undefined
    }
  }, [repo, refInfoForCodeNav, path, user, blob.rawLines, symbols, blob.language, normalizedStylingDirectives, isPlain])

  // Sync validCodeNav state based on whether codeNavInfo was successfully created
  useEffect(() => {
    setValidCodeNav(codeNavInfo !== undefined)
  }, [codeNavInfo, setValidCodeNav])

  const [searchingText, setSearchingText] = useState<CodeNavData>(() => {
    const anchInfo = parseFileAnchor(hash)
    if (!anchInfo.blobRange?.start?.line) {
      return {selectedText: '', lineNumber: -1, offset: 0}
    }

    const isTextRangeOnSingleLineHighlighted =
      !hasBlame &&
      anchInfo.blobRange.start.line === anchInfo.blobRange.end.line &&
      anchInfo.blobRange.start.column !== null &&
      anchInfo.blobRange.end.column !== null &&
      anchInfo.blobRange.end.column - anchInfo.blobRange.start.column > 2 &&
      blob.stylingDirectives &&
      blob.stylingDirectives[anchInfo.blobRange.start.line - 1]?.length &&
      codeNavInfo?.blobLines[anchInfo.blobRange.start.line - 1]

    if (isTextRangeOnSingleLineHighlighted) {
      const highlightedText = codeNavInfo.blobLines[anchInfo.blobRange.start.line - 1]?.substring(
        anchInfo.blobRange.start.column! - 1,
        anchInfo.blobRange.end.column! - 1,
      )

      const stylingDirective = normalizedStylingDirectives![anchInfo.blobRange.start.line - 1]?.find(
        directive =>
          directive.s === anchInfo.blobRange.start.column! - 1 && directive.e === anchInfo.blobRange.end.column! - 1,
      )

      if (highlightedText && stylingDirective && isSymbol(highlightedText, stylingDirective.c)) {
        return {
          selectedText: highlightedText,
          lineNumber: anchInfo.blobRange.start.line,
          offset: anchInfo.blobRange.start.column!,
        }
      } else {
        return {selectedText: '', lineNumber: -1, offset: 0}
      }
    } else if (hash && codeNavInfo && !hasBlame) {
      const sym = codeNavInfo.getSymbolOnLine(Number(hash.substring(2)))
      if (!sym) {
        return {selectedText: '', lineNumber: -1, offset: 0}
      }

      return {selectedText: sym.name, lineNumber: sym.lineNumber, offset: sym.ident.start.column}
    } else {
      return {selectedText: '', lineNumber: -1, offset: 0}
    }
  })

  const showCodeNavWithSymbol = useCallback(
    (sym: CodeSymbol) => {
      setSearchingText({selectedText: sym.name, lineNumber: sym.lineNumber, offset: sym.ident.start.column})
      onCodeNavActivated()

      nav(sym.href())
      scrollLineIntoView({line: sym.lineNumber})
    },
    [onCodeNavActivated, nav],
  )

  const showCodeNavForToken = useCallback(
    (codeNavData: CodeNavData) => {
      setSearchingText(codeNavData)
      onCodeNavActivated()
    },
    //TODO: this is causing a lot of re-renders for a rare edge case
    //figure out how to have the callback get updated properly without relying on this
    //dependency array
    [onCodeNavActivated],
  )

  return {
    isCodeNavLoading,
    codeNavInfo,
    showCodeNavWithSymbol,
    showCodeNavForToken,
    setSearchingText,
    searchingText,
  }
}
